import { lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { theme } from './theme/theme';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import LazyWrapper from './components/LazyWrapper';
import { preloadCriticalComponents } from './utils/preload';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Lazy load pages for code splitting
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SandboxPage = lazy(() => import('./pages/SandboxPage'));
const LoggedHoursPage = lazy(() => import('./pages/LoggedHoursPage'));
const ClientSetupPage = lazy(() => import('./pages/ClientSetupPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ProjectsPage = lazy(() => import('./pages/Projects'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));

function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  // Onboarding redirect logic
  const user = useSelector((state: any) => state.appData.user);
  const loading = useSelector((state: any) => state.appData.loading);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user.status === 'Onboarding' && location.pathname !== '/onboarding') {
      navigate('/onboarding', { replace: true });
    }
  }, [user.status, loading, location.pathname, navigate]);

  return (
    <>
      {!isLoginPage && <Navigation />}
      <Box component="main" sx={{ flex: 1 }}>
        <Routes>
          <Route 
            path="/login" 
            element={
              <LazyWrapper>
                <LoginPage />
              </LazyWrapper>
            } 
          />
          <Route
            path="/sandbox"
            element={
              <LazyWrapper>
                <SandboxPage />
              </LazyWrapper>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <WelcomePage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <ProfilePage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/logged-hours"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <LoggedHoursPage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/setup-client"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <ClientSetupPage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <ClientsPage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <ProjectsPage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <OnboardingPage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="/projects/:projectId" element={<ProjectDetails />} />
          {/* Catch-all route - redirect unknown paths */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <LazyWrapper>
                  <WelcomePage />
                </LazyWrapper>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
      {!isLoginPage && <Footer />}
    </>
  );
}

function AppWrapper() {
  useEffect(() => {
    preloadCriticalComponents();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
            }}
          >
            <AppContent />
          </Box>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default AppWrapper;
